// Returns "/user" for user-scoped tokens, "" for company-scoped tokens.
// The API serves user-scoped routes under the /user prefix.
function scopePrefix() {
  return sessionStorage.getItem("field_token_scope") === "user" ? "/user" : "";
}

// Returns "?company_uuid=<uuid>" for user-scoped tokens, "" for company-scoped.
// Company-scoped routes derive the company from the caller's JWT and reject
// an explicit company_uuid param, so it must never be sent in that scope —
// even when an override uuid is passed in.
function companyUuidParam(overrideUuid) {
  if (scopePrefix() !== "/user") return "";
  const uuid = overrideUuid || sessionStorage.getItem("field_company_uuid");
  return uuid ? `?company_uuid=${uuid}` : "";
}

// Same company-scope guard as companyUuidParam, but returns the bare uuid
// (or "") for callers that build their own query string instead of a
// leading "?" fragment.
function scopedCompanyUuid(overrideUuid) {
  if (scopePrefix() !== "/user") return "";
  return overrideUuid || sessionStorage.getItem("field_company_uuid") || "";
}

export default {
  // ============================================
  // ROLES API
  // ============================================

  getRoles: async function () {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}${scopePrefix()}/role`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  createRole: async function (roleData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/role${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(roleData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  updateRole: async function (uuid, roleData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/role/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(roleData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  deleteRole: async function (uuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/role/${uuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  // ============================================
  // USERS API
  // ============================================

  getUsers: async function (companyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const param = companyUuidParam(companyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user${param}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  getUser: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}${companyUuidParam()}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  createUser: async function (userData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(userData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  updateUser: async function (uuid, userData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(userData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return {
          status: response.status,
          data: json,
        };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  deleteUser: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}${companyUuidParam()}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  inviteUser: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const isUserScoped = scopePrefix() === "/user";
    const invitePath = isUserScoped ? "admin-invite" : "invite";
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${uuid}/${invitePath}${companyUuidParam()}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
          "COMPUTEREASE-DESKTOP-VERSION": "portal",
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  bulkUpdateUsers: async function (userUpdates) {
    const auth_token = await sessionStorage.getItem("token");

    // Execute all updates in parallel
    const promises = userUpdates.map((update) => {
      // Each company-user row belongs to a specific company, so the
      // company_uuid must come from that row, not the globally-selected
      // company context — otherwise a user with rows in multiple companies
      // gets the wrong company_uuid on some rows and the API 404s.
      const companyParam = companyUuidParam(update.companyUuid);
      const request = new Request(
        `${process.env.REACT_APP_API_BASE_URL}${scopePrefix()}/company-user/${
          update.uuid
        }${companyParam}`,
        {
          method: "PUT",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: auth_token,
          }),
          body: JSON.stringify(update.data),
        }
      );

      return fetch(request)
        .then(async (response) => {
          const json = await response.json();
          if (!response.ok) {
            return {
              uuid: update.uuid,
              status: response.status,
              success: false,
              data: json,
            };
          }
          return {
            uuid: update.uuid,
            status: json.status || response.status,
            success: true,
            data: json.data || json,
          };
        })
        .catch(() => ({
          uuid: update.uuid,
          status: 0,
          success: false,
          data: null,
        }));
    });

    try {
      const results = await Promise.all(promises);
      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      return {
        status: failed === 0 ? 200 : 207, // 207 = Multi-Status
        data: {
          successful,
          failed,
          results,
        },
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  bulkInviteUsers: async function (userUuids) {
    const auth_token = await sessionStorage.getItem("token");
    const isUserScoped = scopePrefix() === "/user";
    const invitePath = isUserScoped ? "admin-invite" : "invite";

    // Execute all invites in parallel. Entries may be a plain uuid string or
    // { uuid, companyUuid } — the latter is required for a company-user row
    // that isn't in the globally-selected company context (see bulkUpdateUsers).
    const promises = userUuids.map((entry) => {
      const uuid = typeof entry === "string" ? entry : entry.uuid;
      const companyUuid = typeof entry === "string" ? null : entry.companyUuid;
      const companyParam = companyUuidParam(companyUuid);
      const request = new Request(
        `${
          process.env.REACT_APP_API_BASE_URL
        }${scopePrefix()}/company-user/${uuid}/${invitePath}${companyParam}`,
        {
          method: "PUT",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: auth_token,
            "COMPUTEREASE-DESKTOP-VERSION": "portal",
          }),
        }
      );

      return fetch(request)
        .then((response) => {
          if (!response.ok) {
            return {
              uuid,
              status: response.status,
              success: false,
              data: null,
            };
          }
          return response.json().then((json) => ({
            uuid,
            status: json.status || response.status,
            success: true,
            data: json.data || json,
          }));
        })
        .catch(() => ({
          uuid,
          status: 0,
          success: false,
          data: null,
        }));
    });

    try {
      const results = await Promise.all(promises);
      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      return {
        status: failed === 0 ? 200 : 207, // 207 = Multi-Status
        data: {
          successful,
          failed,
          results,
        },
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  // ============================================
  // LICENSES API
  // ============================================

  getLicenses: async function () {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/license/availability${companyUuidParam()}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  getCompanies: async function () {
    const auth_token = sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/authorization`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || null,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getMyPermissions: async function () {
    const auth_token = sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/user/session`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      return {
        status: json.status || response.status,
        data: json.data || null,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getGroup: async function (uuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const path = `${scopePrefix()}/group/${uuid}${companyParam}`;
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}${path}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  createGroup: async function (groupData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(groupData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  updateGroup: async function (uuid, groupData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(groupData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  deleteGroup: async function (uuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group/${uuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // APPROVAL TEAMS API
  // ============================================

  // options.limit only applies to /company (defaults to 30 there); /user's
  // Joi schema rejects unknown keys and is already unpaginated.
  getApprovalTeams: async function (targetCompanyUuid, options) {
    const auth_token = await sessionStorage.getItem("token");
    const isUserScoped = scopePrefix() === "/user";
    const params = new URLSearchParams();
    if (isUserScoped && targetCompanyUuid) {
      params.set("company_uuid", targetCompanyUuid);
    }
    if (!isUserScoped && options?.limit) {
      params.set("limit", options.limit);
    }
    const companyParam = params.toString() ? `?${params.toString()}` : "";
    const path = `${scopePrefix()}/approval-team${companyParam}`;
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}${path}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  getApprovalTeam: async function (uuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const path = `${scopePrefix()}/approval-team/${uuid}${companyParam}`;
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}${path}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  createApprovalTeam: async function (approvalTeamData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/approval-team${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(approvalTeamData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  updateApprovalTeam: async function (
    uuid,
    approvalTeamData,
    targetCompanyUuid
  ) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/approval-team/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(approvalTeamData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // deleteOptions: { new_approval_team_uuid } — omit/null to clear the team
  // assignment on downstream jobs/entries and mark pending entries approved.
  deleteApprovalTeam: async function (uuid, deleteOptions, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/approval-team/${uuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(deleteOptions || {}),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        return { status: response.status, data: errJson };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // JOB ASSIGNMENTS API (jobs live in api_catalog)
  // ============================================

  getJobs: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/job?${companyParam}${searchParam}limit=1000`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // updates: [{ uuid, approvalTeamUuid, companyUuid }]. Mirrors
  // bulkUpdateUsers — no bulk endpoint exists in api_catalog, so this fires
  // one PUT /catalog/job/{uuid} per row in parallel.
  assignJobApprovalTeams: async function (updates) {
    const auth_token = await sessionStorage.getItem("token");

    const promises = updates.map((update) => {
      const companyUuid = scopedCompanyUuid(update.companyUuid);
      const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
      const request = new Request(
        `${process.env.REACT_APP_API_BASE_URL}/catalog/job/${update.uuid}${companyParam}`,
        {
          method: "PUT",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: auth_token,
          }),
          body: JSON.stringify({
            approval_team_uuid: update.approvalTeamUuid || null,
          }),
        }
      );

      return fetch(request)
        .then(async (response) => {
          const json = await response.json().catch(() => null);
          if (!response.ok) {
            return {
              uuid: update.uuid,
              status: response.status,
              success: false,
              data: json,
            };
          }
          return {
            uuid: update.uuid,
            status: json?.status || response.status,
            success: true,
            data: json?.data || json,
          };
        })
        .catch(() => ({
          uuid: update.uuid,
          status: 0,
          success: false,
          data: null,
        }));
    });

    try {
      const results = await Promise.all(promises);
      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      return {
        status: failed === 0 ? 200 : 207,
        data: { successful, failed, results },
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  updateJobLocation: async function (
    uuid,
    { latitude, longitude, location_address, radius, polygon_coords },
    targetCompanyUuid
  ) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/job/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify({
          latitude,
          longitude,
          location_address,
          radius,
          polygon_coords,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json().catch(() => null);

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json?.status || response.status,
        data: json?.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // API KEYS API (company_key — company-scope only, no /user wrapper)
  // ============================================

  getApiKeys: async function () {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/company-key`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  createApiKey: async function (apiKeyData) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/company-key`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(apiKeyData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  updateApiKey: async function (uuid, apiKeyData) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/company-key/${uuid}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(apiKeyData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  deleteApiKey: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/company-key/${uuid}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return { status: response.status, data: null };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  reverifyApiKey: async function (uuid) {
    const auth_token = await sessionStorage.getItem("token");
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/company-key/${uuid}/reverify`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();

      if (!response.ok) {
        return { status: response.status, data: json };
      }

      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // CATALOG LOOKUPS (api_catalog)
  // ============================================
  // These call the catalog service (REACT_APP_API_BASE_URL/catalog/*) rather
  // than the identity service, to fetch company-defined code lists used to
  // populate checkbox-style group filter modals (e.g. Cost Types).

  getCostTypes: async function (targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/cost-type?${companyParam}limit=1000`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getExpenseTypes: async function (targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/expense-type?${companyParam}limit=1000`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Liability GL accounts only — used by the Expense Cards GL Account picker.
  getLiabilityGlAccounts: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/gl-account?${companyParam}${searchParam}type=liability&limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getPayrollDepartments: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/payroll-department?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getWorkerClasses: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/worker-class?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getEquipments: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/equipment?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getEmployees: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/employee?${companyParam}${searchParam}sort=code&limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getEquipmentCodes: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/equipment-code?${companyParam}${searchParam}sort=code&limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getInventoryItems: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/inventory-item?${companyParam}${searchParam}sort=code&limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // include_related=false skips the vendor list's GL/subcontract joins.
  getVendors: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/vendor?${companyParam}${searchParam}sort=code&include_related=false&limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Service work orders live in api_service_management, not api_catalog —
  // path is v1/service-mgmt/service-work-order; rows key on order_number.
  getServiceWorkOrders: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/service-mgmt/service-work-order?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Service persons live in api_service_management; rows key on serviceperson.
  getServicePersons: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/service-mgmt/service-person?${companyParam}${searchParam}sort=serviceperson&limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getAbsenceCodes: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/absence-code?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Field Log Types live in api_field_log, not api_catalog — the
  // v1/field-log/field-log-type path (confirmed against mobile_ce's
  // api_fieldlog_service.dart), not v1/catalog/*.
  getFieldLogTypes: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/field-log/field-log-type?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getUnions: async function (search, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `company_uuid=${companyUuid}&` : "";
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/union?${companyParam}${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // workers-comp's list route (api_catalog) does not accept a company_uuid
  // query param — company scope comes from the auth token identity, and the
  // Joi params schema rejects unknown keys, so it must be omitted here.
  getWorkersComps: async function (search) {
    const auth_token = await sessionStorage.getItem("token");
    const searchParam = search ? `search=${encodeURIComponent(search)}&` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/catalog/workers-comp?${searchParam}limit=50`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // EXPENSE CARDS API (purchase methods, api_expense)
  // ============================================

  getExpenseCards: async function (companyUserUuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const params = [`company_user_uuid=${companyUserUuid}`, "limit=1000"];
    if (companyUuid) params.push(`company_uuid=${companyUuid}`);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }/expense/purchase-method?${params.join("&")}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  createExpenseCard: async function (cardData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/expense/purchase-method${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(cardData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  updateExpenseCard: async function (uuid, cardData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/expense/purchase-method/${uuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(cardData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  deleteExpenseCard: async function (uuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/expense/purchase-method/${uuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // ACH VAULT API (api_ach_vault) — a separate service on its own base URL
  // (REACT_APP_ACH_API_BASE_URL), not REACT_APP_API_BASE_URL. It never
  // returns routing/account numbers to this scope; only a masked projection.
  // ============================================

  getUserAch: async function (companyUserUuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_ACH_API_BASE_URL}/ach/${companyUserUuid}${companyParam}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Full upsert — replaces the secret payload (routing/account number) and
  // all attributes. Used only when the admin has typed a new account number.
  saveUserAch: async function (companyUserUuid, achData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_ACH_API_BASE_URL}/ach/${companyUserUuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(achData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Non-secret attributes only (account type, prenote, enabled) — never sends
  // a routing/account number, so an untouched secret is never re-typed just
  // to flip Prenote.
  saveUserAchAttributes: async function (
    companyUserUuid,
    attributes,
    targetCompanyUuid
  ) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_ACH_API_BASE_URL}/ach/${companyUserUuid}/attributes${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(attributes),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  deleteUserAch: async function (companyUserUuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyUuid = scopedCompanyUuid(targetCompanyUuid);
    const companyParam = companyUuid ? `?company_uuid=${companyUuid}` : "";
    const request = new Request(
      `${process.env.REACT_APP_ACH_API_BASE_URL}/ach/${companyUserUuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // Non-secret mirror only, written to api_identity (not the vault) so
  // read-only views (UserDetail) can show ACH status without a vault call.
  // Call only after a successful vault write — never send routing/account
  // numbers here.
  updateAchStatus: async function (
    companyUserUuid,
    achStatus,
    targetCompanyUuid
  ) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/company-user/${companyUserUuid}/ach-status${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify({
          pay_expenses_via_ach: achStatus.payExpensesViaAch,
          ach_updated_at: achStatus.achUpdatedAt,
        }),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  // ============================================
  // GROUP FILTERS API
  // ============================================

  getGroupFilters: async function (groupUuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group/${groupUuid}/filters${companyParam}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  upsertGroupFilter: async function (groupUuid, filterData, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group/${groupUuid}/filters${companyParam}`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(filterData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  updateGroupFilter: async function (
    groupUuid,
    filterUuid,
    filterData,
    targetCompanyUuid
  ) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group/${groupUuid}/filters/${filterUuid}${companyParam}`,
      {
        method: "PUT",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
        body: JSON.stringify(filterData),
      }
    );

    try {
      const response = await fetch(request);
      const json = await response.json();
      if (!response.ok) return { status: response.status, data: json };
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  deleteGroupFilter: async function (groupUuid, filterUuid, targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const companyParam = companyUuidParam(targetCompanyUuid);
    const request = new Request(
      `${
        process.env.REACT_APP_API_BASE_URL
      }${scopePrefix()}/group/${groupUuid}/filters/${filterUuid}${companyParam}`,
      {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);
      if (!response.ok) return { status: response.status, data: null };
      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return { status: 0, data: null };
    }
  },

  getGroups: async function (targetCompanyUuid) {
    const auth_token = await sessionStorage.getItem("token");
    const isUserScoped = scopePrefix() === "/user";
    const companyParam =
      isUserScoped && targetCompanyUuid
        ? `?company_uuid=${targetCompanyUuid}`
        : "";
    const path = `${scopePrefix()}/group${companyParam}`;
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}${path}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },

  // ============================================
  // AUDIT HISTORY API
  // ============================================

  // resource is one of "roles", "company_users", "groups", "approval_teams",
  // "company_keys", "job_locations". service is the writer's audit service
  // segment — "identity" for everything api_identity writes, "catalog" for
  // job_locations (written by api_catalog).
  getAuditHistory: async function (resource, uuid, service = "identity") {
    const auth_token = await sessionStorage.getItem("token");
    // A user-scoped admin's token can carry several companies; pin the lookup
    // to the company currently being administered so the record is found
    // regardless of which company happens to be first on the token.
    const companyUuid = scopedCompanyUuid();
    const query = companyUuid
      ? `?company_uuid=${encodeURIComponent(companyUuid)}`
      : "";
    const request = new Request(
      `${process.env.REACT_APP_API_BASE_URL}/logs/audits/${service}/${resource}/${uuid}${query}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: auth_token,
        }),
      }
    );

    try {
      const response = await fetch(request);

      if (!response.ok) {
        return {
          status: response.status,
          data: null,
        };
      }

      const json = await response.json();
      return {
        status: json.status || response.status,
        data: json.data || json,
      };
    } catch (err) {
      return {
        status: 0,
        data: null,
      };
    }
  },
};
